import type { CommandPaletteTarget } from 'features/commandPalette/utils/commandPaletteTargets'
import { EVALUATIONS_PERMISSIONS } from 'features/evaluations/perms/evaluationsPermissions'
import { MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST } from 'features/manageUsers/manageUserPermissionsPermissionsList'
import {
  evaluationsHref,
  orgSiteHref,
  WORKSPACE_SETTINGS_DESTINATION,
} from 'features/navbar/configSettings/workspaceSettingsLink'
import { useGeckoPermissions } from 'features/permissions'
import { Settings } from 'lucide-react'
import { useMemo } from 'react'
import {
  PERMISSION_GECKO_COST_METRICS_VIEW,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  type RolodexPermissionsValues,
} from 'types'

export const SETTINGS_KIND_LABEL = 'Settings'
const DESIGN_SYSTEM_LIBRARY_LABEL = 'Design System Library'

interface SettingsEntry {
  destinationKey: string
  href: string
  requires: ReadonlyArray<RolodexPermissionsValues>
  /** The settings menu itself only renders for internal members. */
  internalOnly: boolean
  reuseMode?: 'focus-and-navigate'
}

/**
 * The settings and profile-menu pages, mirroring the sidebar's footer menus
 * item for item, including their permission gates.
 */
export function useCommandPaletteSettingsTargets({
  siteId,
  orgId,
}: {
  siteId: string | undefined
  orgId: string | undefined
}): CommandPaletteTarget[] {
  const { hasPermissions } = useGeckoPermissions()

  return useMemo(() => {
    const isInternal = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])
    const entries: SettingsEntry[] = [
      {
        destinationKey: WORKSPACE_SETTINGS_DESTINATION.appConfig,
        href: '/app-config',
        requires: [],
        internalOnly: true,
      },
      ...(orgId
        ? [
            {
              destinationKey: WORKSPACE_SETTINGS_DESTINATION.manageUsers,
              href: orgSiteHref('/users', orgId, siteId),
              requires: MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST,
              internalOnly: true,
            },
            {
              destinationKey: WORKSPACE_SETTINGS_DESTINATION.manageExtensions,
              href: orgSiteHref('/extensions', orgId, siteId),
              requires: [],
              internalOnly: true,
            },
          ]
        : []),
      {
        destinationKey: WORKSPACE_SETTINGS_DESTINATION.managePermissions,
        href: '/admin/users',
        requires: [],
        internalOnly: true,
      },
      {
        destinationKey: WORKSPACE_SETTINGS_DESTINATION.healthCheck,
        href: '/health-check',
        requires: [],
        internalOnly: true,
      },
      {
        destinationKey: WORKSPACE_SETTINGS_DESTINATION.costDashboard,
        href: '/cost-dashboard',
        requires: [PERMISSION_GECKO_COST_METRICS_VIEW],
        internalOnly: true,
      },
      {
        destinationKey: DESIGN_SYSTEM_LIBRARY_LABEL,
        href: '/design-system-library',
        requires: [],
        internalOnly: true,
      },
      {
        destinationKey: WORKSPACE_SETTINGS_DESTINATION.evaluations,
        href: evaluationsHref({ organizationId: orgId, siteId }),
        requires: EVALUATIONS_PERMISSIONS,
        internalOnly: false,
        reuseMode: 'focus-and-navigate',
      },
    ]

    return entries
      .filter(
        (entry) =>
          (!entry.internalOnly || isInternal) &&
          (entry.requires.length === 0 || hasPermissions([...entry.requires]))
      )
      .map((entry) => ({
        id: `settings:${entry.destinationKey}`,
        kind: 'settings' as const,
        label: entry.destinationKey,
        kindLabel: SETTINGS_KIND_LABEL,
        icon: Settings,
        href: entry.href,
        destinationKey: entry.destinationKey,
        reuseMode: entry.reuseMode,
      }))
  }, [hasPermissions, orgId, siteId])
}
